<template>
    <div class="tw:flex tw:flex-col tw:items-center tw:w-full">
        <div class="tw:flex tw:flex-col tw:items-center tw:w-full tw:mx-auto tw:text-center tw:relative">
            <div
                class="tw:flex tw:items-start tw:justify-between tw:w-full tw:max-w-[1200px] tw:relative tw:flex-nowrap stepper-front-steps-row"
                :style="{ '--total-steps': steps.length }"
            >
                <!-- Línea de fondo (gris): va del centro del primer al centro del último círculo -->
                <div class="tw:absolute tw:h-[2px] tw:bg-[#ddd] tw:z-0 tw:rounded-sm stepper-front-line-bg"></div>
                <!-- Línea de progreso (color acento): misma posición que el fondo,
                     ancho = fracción del espacio entre centros de los círculos extremos.
                     Fórmula: progressFraction * (100% - circle-size) -->
                <div
                    class="tw:absolute tw:h-[2px] tw:z-[1] tw:rounded-sm stepper-front-line-progress"
                    :style="{ '--progress-fraction': progressFraction }"
                ></div>

                <!-- Pasos -->
                <div
                    v-for="(label, index) in steps"
                    :key="index"
                    :id="'stepper-front-step-' + (index + 1)"
                    class="tw:flex tw:flex-col tw:relative tw:z-[2]"
                    :class="{
                        'tw:items-start': index === 0,
                        'tw:items-center': index > 0 && index < steps.length - 1,
                        'tw:items-end': index === steps.length - 1,
                    }"
                >
                    <!-- Círculo indicador -->
                    <div
                        class="stepper-front-circle tw:bg-white tw:border-[2px] tw:rounded-full tw:flex tw:items-center tw:justify-center tw:transition-all tw:duration-300 tw:ease-in-out tw:z-[3] stepper-front-circle--future"
                        :class="{
                            'stepper-front-circle--active': isActiveOrDone(index),
                            'stepper-front-circle--pulsing': isActive(index),
                        }"
                    >
                        <!-- Check cuando el paso ya está completado -->
                        <i v-if="isCompleted(index)" class="material-icons tw:leading-none stepper-front-check">check</i>
                        <!-- Número del paso -->
                        <span
                            v-else
                            class="tw:leading-none stepper-front-number"
                        >{{ index + 1 }}</span>
                    </div>

                    <!-- Etiqueta -->
                    <span
                        class="tw:mt-2 tw:text-[#aaa] tw:font-semibold tw:transition-colors tw:duration-300 tw:ease-in-out tw:whitespace-nowrap tw:leading-[1.2] stepper-front-label"
                        :class="{
                            'tw:!text-[#444444] tw:!font-bold': isActiveOrDone(index),
                            'tw:text-left': index === 0,
                            'tw:text-center': index > 0 && index < steps.length - 1,
                            'tw:text-right': index === steps.length - 1,
                        }"
                    >
                        {{ label }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import { useStepper } from '@/composables/front/common/useStepper';

    const props = defineProps({
        /** Etiquetas de cada paso, en orden. */
        steps: {
            type: Array,
            required: true,
        },
        /**
         * Paso activo (1-indexed).
         * Úsalo con v-model:current-step desde el padre o como prop unidireccional.
         */
        currentStep: {
            type: Number,
            required: true,
        },
    });

    const emit = defineEmits(['update:currentStep']);

    const { progressFraction, isCompleted, isActive, isActiveOrDone } = useStepper(
        computed(() => props.steps),
        computed(() => props.currentStep),
    );
</script>


<style scoped>
/* ── Variable del tamaño del círculo — única fuente de verdad para top de la línea ── */
.stepper-front-steps-row {
    --circle-size: calc(clamp(26px, 5vw, 40px) - 5px);
}

/* ── clamp() en círculo — no expresable en Tailwind sin config custom ── */
.stepper-front-circle {
    width: var(--circle-size);
    height: var(--circle-size);
    font-size: clamp(12px, 3vw, 20px);
}

/* ── Estado futuro (no alcanzado) — mismo gris que la línea de fondo ── */
.stepper-front-circle--future {
    border-color: #ddd;
}

.stepper-front-circle--active {
    background-color: var(--altcolor) !important;
    border-color: var(--altcolor) !important;
    font-size: clamp(12px, 3vw, 18px);
}

/* ── clamp() en iconos/número ── */
.stepper-front-check {
    font-size: clamp(10px, 2.5vw, 16px) !important;
    color: var(--altcolor);
}

.stepper-front-number {
    font-size: clamp(10px, 2.5vw, 16px);
    color: #333;
}

/* ── clamp() en etiqueta ── */
.stepper-front-label {
    font-size: clamp(9px, 2.3vw, 14px);
}

/* ── Líneas: van del centro del primer círculo al centro del último ── */
/*    Con los círculos extremos alineados a los bordes (items-start / items-end),      */
/*    el centro del primer círculo está a circle-size/2 del borde izquierdo             */
/*    y el centro del último a circle-size/2 del borde derecho.                          */
.stepper-front-line-bg,
.stepper-front-line-progress {
    left: calc(var(--circle-size) / 2);
    right: calc(var(--circle-size) / 2);
    top: calc(var(--circle-size) / 2 - 2px);
}

/* La línea de progreso sobreescribe right con width para crecer desde la izquierda */
.stepper-front-line-progress {
    right: auto;
    /* width = fracción × espacio entre centros = fraction × (100% − circle-size) */
    width: calc(var(--progress-fraction) * (100% - var(--circle-size)));
    background-color: var(--altcolor);
    transition: width 0.5s ease-in-out;
}

/* ── Pseudo-elemento pulsing — no expresable en Tailwind ── */
.stepper-front-circle--pulsing {
    position: relative;
}

.stepper-front-circle--pulsing::after {
    content: '';
    position: absolute;
    width: clamp(32px, 5vw, 44px);
    height: clamp(32px, 5vw, 44px);
    border-radius: 50%;
    border: 2px solid var(--altcolor);
    animation: stepper-front-pulse 2s infinite;
}

@keyframes stepper-front-pulse {
    0% {
        transform: scale(1);
        opacity: 0.7;
    }
    100% {
        transform: scale(1.2);
        opacity: 0;
    }
}

/* ── Responsive — clamp() en círculo ── */
@media (max-width: 600px) {
    .stepper-front-steps-row {
        --circle-size: calc(clamp(24px, 5vw, 32px) - 4px);
    }

    .stepper-front-circle {
        font-size: clamp(11px, 2.5vw, 16px);
    }

    .stepper-front-circle--active {
        font-size: clamp(12px, 3vw, 17px);
    }

    .stepper-front-label {
        font-size: clamp(9px, 2.2vw, 13px);
    }

    .stepper-front-number {
        font-size: 13px;
    }
}
</style>
